"use client";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

function ResetPassword() {
  const navigate = useNavigate();

  const [formData, setFormData] = React.useState({
    newPassword: "",
    confirmPassword: ""
  });

  const [errors, setErrors] = React.useState({
    newPassword: "",
    confirmPassword: ""
  });

  const [showModal, setShowModal] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showPasswords, setShowPasswords] = React.useState({
    newPassword: false,
    confirmPassword: false
  });

  const passwordRequirements = {
    minLength: 8,
    hasUpperCase: /[A-Z]/,
    hasLowerCase: /[a-z]/,
    hasNumber: /\d/,
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < passwordRequirements.minLength)
      errors.push(`Password must be at least ${passwordRequirements.minLength} characters`);
    if (!passwordRequirements.hasUpperCase.test(password))
      errors.push("Password must contain an uppercase letter");
    if (!passwordRequirements.hasLowerCase.test(password))
      errors.push("Password must contain a lowercase letter");
    if (!passwordRequirements.hasNumber.test(password))
      errors.push("Password must contain a number");
    if (!passwordRequirements.hasSpecialChar.test(password))
      errors.push("Password must contain a special character");
    return errors;
  };

  const validateForm = () => {
    const newErrors = { newPassword: "", confirmPassword: "" };

    if (!formData.newPassword.trim()) newErrors.newPassword = "New password is required";
    else {
      const passErrors = validatePassword(formData.newPassword);
      if (passErrors.length) newErrors.newPassword = passErrors[0];
    }

    if (!formData.confirmPassword.trim()) newErrors.confirmPassword = "Confirm password is required";
    else if (formData.newPassword !== formData.confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";

    setErrors(newErrors);
    return !newErrors.newPassword && !newErrors.confirmPassword;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    if (field === "newPassword" && formData.confirmPassword === value)
      setErrors((prev) => ({ ...prev, confirmPassword: "" }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setShowModal(true);
      setFormData({ newPassword: "", confirmPassword: "" });
    }, 1000);
  };

  const handleSignIn = () => navigate("/login");
  const handleReturnToStore = () => navigate("/");
  const closeModal = () => {
    setShowModal(false);
    navigate("/login");
  };

  const underlineVariants = {
    initial: { scaleX: 0 },
    hover: { scaleX: 1, transition: { duration: 0.3, ease: "easeInOut" } },
    exit: { scaleX: 0, transition: { duration: 0.3, ease: "easeInOut" } }
  };

  const AnimatedLink = ({ children, onClick, className = "" }) => (
    <motion.button
      onClick={onClick}
      className={`relative text-yellow-500 hover:opacity-80 bg-transparent border-none cursor-pointer p-0 outline-none ${className}`}
      whileHover="hover"
      initial="initial"
      animate="initial"
    >
      <span className="relative z-10">{children}</span>
      <motion.span
        className="absolute bottom-0 left-0 w-full h-0.5 bg-yellow-400 origin-center"
        variants={underlineVariants}
      />
    </motion.button>
  );

  const PasswordInput = ({ id, value, onChange, placeholder, error, showPassword, onToggleVisibility }) => (
    <div className="flex flex-col w-full">
      <div className={`flex items-center px-4 py-3 border rounded-lg transition-colors focus-within:ring-2 focus-within:ring-yellow-300 ${error ? "border-red-500" : "border-yellow-300"}`}>
        <input
          id={id}
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-transparent outline-none text-stone-700 placeholder:text-stone-400"
        />
        <button
          type="button"
          onClick={onToggleVisibility}
          className="ml-2 text-yellow-500 hover:text-yellow-600 focus:outline-none"
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? "🙈" : "👁️"}
        </button>
      </div>
      {error && <span className="text-red-500 text-sm mt-1">{error}</span>}
    </div>
  );

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="flex flex-col gap-10 p-10 rounded-3xl shadow-lg w-full max-w-md">
          <header className="text-center">
            <h1 className="text-4xl font-bold text-yellow-500">Reset Password</h1>
            <p className="text-gray-600 mt-2 text-sm">Set a strong new password</p>
          </header>

          <div className="flex justify-center gap-2 text-gray-700 text-sm">
            <span>Remembered your password?</span>
            <AnimatedLink onClick={handleSignIn}>Sign in here</AnimatedLink>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5 w-full" noValidate>
            <PasswordInput
              id="newPassword"
              value={formData.newPassword}
              onChange={(value) => handleInputChange("newPassword", value)}
              placeholder="New Password"
              error={errors.newPassword}
              showPassword={showPasswords.newPassword}
              onToggleVisibility={() => togglePasswordVisibility("newPassword")}
            />
            <PasswordInput
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={(value) => handleInputChange("confirmPassword", value)}
              placeholder="Confirm Password"
              error={errors.confirmPassword}
              showPassword={showPasswords.confirmPassword}
              onToggleVisibility={() => togglePasswordVisibility("confirmPassword")}
            />

            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-3 bg-yellow-400 hover:bg-yellow-500 text-white font-semibold rounded-lg shadow transition-all disabled:opacity-50"
            >
              {isSubmitting ? "Updating..." : "Update Password"}
            </button>

            <div className="flex justify-center pt-4">
              <AnimatedLink onClick={handleReturnToStore}>Return to Store</AnimatedLink>
            </div>
          </form>
        </div>
      </div>

      {showModal && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="bg-white rounded-2xl p-8 max-w-md w-full shadow-lg relative"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-yellow-100 mb-4">
                <svg
                  className="h-7 w-7 text-yellow-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-yellow-500 mb-2">Password Updated!</h2>
              <p className="text-gray-600 mb-6">
                Your password has been successfully updated. You can now sign in with your new password.
              </p>
              <button
                onClick={closeModal}
                className="w-full px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-white rounded-lg transition-all"
              >
                Sign In Now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}

export default ResetPassword;
